import React, { useMemo, useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
    Box, Typography, Chip, TextField, Tooltip, Button
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Subcontractor } from '../types/subcontractTypes';
import { SubcontractPermissions } from '../contexts/constants/subcontractPermissions';
import { formatRUT } from '../utils/validation';
import ColumnFilterMenu from './ColumnFilterMenu';
import { exportRowsToExcel, todayStamp } from '../utils/exportExcel';

interface SubcontractTableProps {
    subcontractors: Subcontractor[];
    permissions: SubcontractPermissions;
    onEdit: (subcontractor: Subcontractor) => void;
    onDelete: (subcontractor: Subcontractor) => void;
}

const HISTORY_BLOCKED_MESSAGE = 'No se puede eliminar: Empresa ya tiene datos de HH/HM registrados.';

const resourceTypeLabel = (resourceType: string) => (resourceType === 'PERSONNEL' ? 'Personal' : 'Equipos');

/**
 * Columnas filtrables. `values` devuelve una lista porque Especialidades, Proyectos y
 * Recurso son multivaluados: la fila coincide si alguno de sus valores está seleccionado.
 */
const FILTERABLE_COLUMNS: { id: string; label: string; values: (item: Subcontractor) => string[] }[] = [
    { id: 'rut', label: 'RUT EMPRESA', values: item => [formatRUT(item.rut)] },
    { id: 'businessName', label: 'RAZÓN SOCIAL', values: item => [item.businessName] },
    { id: 'sections', label: 'ESPECIALIDADES / SERVICIOS', values: item => item.sections || [] },
    { id: 'projects', label: 'PROYECTOS ASIGNADOS', values: item => (item.projects || []).map(p => p.name) },
    { id: 'resourceTypes', label: 'RECURSO', values: item => (item.resourceTypes || []).map(resourceTypeLabel) },
    { id: 'operationalStatus', label: 'ESTADO', values: item => [item.operationalStatus ? 'Activo' : 'Inactivo'] },
];

const SubcontractTable: React.FC<SubcontractTableProps> = ({
    subcontractors,
    permissions,
    onEdit,
    onDelete
}) => {
    const [filter, setFilter] = useState('');
    const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
        () => FILTERABLE_COLUMNS.reduce((acc, col) => ({ ...acc, [col.id]: [] }), {} as Record<string, string[]>)
    );
    const [filterMenu, setFilterMenu] = useState<{ anchorEl: null | HTMLElement; columnKey: string | null }>({
        anchorEl: null,
        columnKey: null,
    });

    const search = filter.trim().toLowerCase();
    const filtered = useMemo(() => subcontractors.filter(item => {
        // --- ETAPA 1: BÚSQUEDA GLOBAL (lógica "O") ---
        if (search) {
            const matchesSearch =
                item.businessName.toLowerCase().includes(search) ||
                item.rut.toLowerCase().includes(search) ||
                formatRUT(item.rut).toLowerCase().includes(search);
            if (!matchesSearch) return false;
        }

        // --- ETAPA 2: FILTROS POR COLUMNA (lógica "Y") ---
        return FILTERABLE_COLUMNS.every(col => {
            const selected = columnFilters[col.id];
            if (!selected || selected.length === 0) return true;
            return col.values(item).some(value => selected.includes(value));
        });
    }), [subcontractors, search, columnFilters]);

    const uniqueValues = useMemo<string[]>(() => {
        const column = FILTERABLE_COLUMNS.find(col => col.id === filterMenu.columnKey);
        if (!column) return [];
        const values = subcontractors.flatMap(column.values).filter(value => !!value);
        return Array.from(new Set(values)).sort();
    }, [subcontractors, filterMenu.columnKey]);

    const handleColumnFilterChange = (selected: string[]) => {
        if (!filterMenu.columnKey) return;
        setColumnFilters(prev => ({ ...prev, [filterMenu.columnKey as string]: selected }));
    };

    const hasActiveFilters = !!filter || Object.values(columnFilters).some(values => values.length > 0);

    const clearFilters = () => {
        setFilter('');
        setColumnFilters(FILTERABLE_COLUMNS.reduce((acc, col) => ({ ...acc, [col.id]: [] }), {} as Record<string, string[]>));
    };

    /** Exporta las empresas tal como se están viendo, respetando los filtros activos. */
    const handleExportExcel = () => {
        const rows = filtered.map(item => ({
            'RUT Empresa': formatRUT(item.rut),
            'Razón Social': item.businessName,
            'Especialidades / Servicios': (item.sections || []).join(', '),
            'Proyectos Asignados': (item.projects || []).map(p => p.name).join(', '),
            'Recurso': (item.resourceTypes || []).map(resourceTypeLabel).join(', '),
            'Estado': item.operationalStatus ? 'Activo' : 'Inactivo',
        }));
        exportRowsToExcel(rows, 'Subcontratistas', `Subcontratistas_${todayStamp()}.xlsx`);
    };

    // Estado inicial del mantenedor: ante la ausencia de registros se omiten las cabeceras
    // de la matriz y se muestra un contenedor con iconografia descriptiva.
    if (subcontractors.length === 0) {
        return (
            <Paper
                sx={{
                    p: 6,
                    mt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    backgroundColor: '#fafafa'
                }}
            >
                <BusinessIcon sx={{ fontSize: 64, color: '#b0bec5' }} />
                <Typography variant="body1" color="textSecondary" align="center">
                    No hay subcontratistas registrados. Haga clic en el botón azul de arriba para
                    agregar la primera empresa.
                </Typography>
            </Paper>
        );
    }

    return (
        <Box>
            <TextField
                label="Buscar por RUT o razón social"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                fullWidth
                margin="normal"
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                    {filtered.length === subcontractors.length
                        ? `${subcontractors.length} empresas`
                        : `${filtered.length} de ${subcontractors.length} empresas`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {hasActiveFilters && (
                        <Typography
                            variant="body2"
                            onClick={clearFilters}
                            sx={{ color: '#004F86', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                            Limpiar filtros
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<FileDownloadOutlinedIcon />}
                        onClick={handleExportExcel}
                    >
                        Exportar Excel
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper} sx={{ width: '100%', maxHeight: 700, overflow: 'auto' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {FILTERABLE_COLUMNS.map(col => (
                                <TableCell key={col.id} sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {col.label}
                                        <IconButton
                                            size="small"
                                            title={`Filtrar por ${col.label}`}
                                            onClick={(e) => setFilterMenu({ anchorEl: e.currentTarget, columnKey: col.id })}
                                            sx={{ color: columnFilters[col.id]?.length > 0 ? '#004F86' : 'inherit' }}
                                        >
                                            <FilterListIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            ))}
                            <TableCell sx={{ color: '#004F86', fontWeight: 'bold' }}>ACCIONES</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.map(item => {
                            // Restriccion por Integridad Historica: con historial de HH/HM el borrado
                            // se deshabilita. En Fase 1 aplica a todos los perfiles; la excepcion para
                            // Gerente y Director de Proyectos queda para la Fase 2.
                            const blockedByHistory = item.hasHistory;
                            const deleteAllowed = permissions.canDeleteWithoutHistory && !blockedByHistory;

                            const deleteTooltip = blockedByHistory
                                ? HISTORY_BLOCKED_MESSAGE
                                : (permissions.canDeleteWithoutHistory
                                    ? 'Eliminar empresa'
                                    : 'Su perfil no permite eliminar empresas');

                            return (
                                <TableRow key={item.id} sx={{ backgroundColor: 'white' }}>
                                    <TableCell>{formatRUT(item.rut)}</TableCell>
                                    <TableCell>{item.businessName}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {item.sections.map(section => (
                                                <Chip
                                                    key={section}
                                                    label={section}
                                                    size="small"
                                                    sx={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 500 }}
                                                />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {item.projects.map(project => (
                                                <Chip
                                                    key={project.id}
                                                    label={project.name}
                                                    size="small"
                                                    sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }}
                                                />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(item.resourceTypes || []).map(resourceType => (
                                                <Chip
                                                    key={resourceType}
                                                    label={resourceType === 'PERSONNEL' ? 'Personal' : 'Equipos'}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: resourceType === 'PERSONNEL' ? '#e3f2fd' : '#fff8e1',
                                                        color: resourceType === 'PERSONNEL' ? '#1565c0' : '#8a6d00',
                                                        fontWeight: 500
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    backgroundColor: item.operationalStatus ? '#2e7d32' : '#d32f2f'
                                                }}
                                            />
                                            <Typography variant="body2">
                                                {item.operationalStatus ? 'Activo' : 'Inactivo'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={permissions.canEditBasicData ? 'Editar empresa' : 'Su perfil no permite editar'}>
                                            <span>
                                                <IconButton
                                                    onClick={() => onEdit(item)}
                                                    disabled={!permissions.canEditBasicData}
                                                    sx={{ color: permissions.canEditBasicData ? 'primary.main' : 'gray' }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={deleteTooltip}>
                                            <span>
                                                <IconButton
                                                    onClick={() => onDelete(item)}
                                                    disabled={!deleteAllowed}
                                                    sx={{ color: deleteAllowed ? 'error.main' : 'gray' }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <ColumnFilterMenu
                anchorEl={filterMenu.anchorEl}
                values={uniqueValues}
                selected={filterMenu.columnKey ? (columnFilters[filterMenu.columnKey] || []) : []}
                onClose={() => setFilterMenu({ anchorEl: null, columnKey: null })}
                onChange={handleColumnFilterChange}
            />
        </Box>
    );
};

export default SubcontractTable;
